import React, {useState, useEffect, Fragment} from "react";
import styled from "styled-components";
import PullToRefresh from "react-simple-pull-to-refresh";
import {useLocation, useNavigate, useParams} from "react-router";
import {useSelector} from "react-redux";

import {api} from "utils";
import {getCommaNumber} from "utils/format";

import MainLayout from "layouts/MainLayout";
import Button from "components/ui/Button";
import Badge from "components/ui/Badge";
import PassInfo from "./PassInfo";
import PaymentForm from "./PaymentForm";
import PullToRefreshing from "components/common/PullToRefreshing";
import {getPaymentTypeName, getServiceTypeColor, getServiceTypeName} from "../../../utils/define";



/**
 * [주문 결제 상세]
 */

export const PaymentInfo = (props) => {
	const navigate = useNavigate();
	const location = useLocation()
	const {seqOrderPayment} = useParams();
	const {serviceType} = location.state || {};

	const [data, setData] = useState({});
	const [isLoading, setLoading] = useState(true);
	const [isError, setError] = useState(false);

	useEffect(() => {
		eventHandler.update();
	}, [seqOrderPayment]);

	const eventHandler = {
		update : () => {
			api.get("/sales/order/details/" + seqOrderPayment).then(({data}) => {
				setData(data || {});
			}).catch(error => {
				setError(true);
			}).finally(() => {
				setLoading(false);
			});
		},
		pullToRefresh: () => {
			return new Promise((resolve) => {
				setTimeout(() => {
					eventHandler.update();
					resolve();
				}, 700);
			});
		}
	};

	const render = () => {
		const isCancel = (data.orderState == "CANCELLED");
		const isRefund = (data.orderType == "TRANSFER");

		const getTitle = () => {
			const orderPayType = (data.webAppDiv == "A") ? "b.pay" : "현장판매";
			const orderName = (data.orderName || "(이름없음)").recoverTag();
			const serviceTypeName = getServiceTypeName(serviceType);
			const serviceTypeColor = getServiceTypeColor(serviceType);
			return (isRefund) ? (
				<div className="title">
					<h5 className="gray">{orderName}</h5>
					<h2>양도 수수료</h2>
				</div>
			) : (
				<div className="title">
					{
						(serviceType) ?
							<h5 className={serviceTypeColor}>{serviceTypeName}</h5> :
							<h5 className="blue">{orderPayType}</h5>
					}
					<h2>{orderName}</h2>
				</div>
			);
		};

		const getPaymentInfo = () => {
			// CS25-8980: 금액 색은 이 결제의 상태(data.paymentStatus)로 정한다. 목록과 같이 결제대기는 회색, 음수 금액은 빨간색이고,
			// 결제대기·결제취소는 파란색(낸 결제)으로 보이지 않는다. 라벨 문구는 그대로다.
			const isWaitPayment = (data.paymentStatus == "PAYMENT_WAIT");
			const isCancelledPayment = (data.paymentStatus == "PAYMENT_CANCELLED");
			const paymentColor = (isWaitPayment) ? "gray" : (data.paymentAmount < 0) ? "red" : (isCancel || isCancelledPayment) ? "" : "blue";
			const paymentAmount = getCommaNumber(data.paymentAmount) + "원";

			const getPaymentDateTime = () => {
				const dateFormat = "yyyy. sm. sd. sw · hh:MM:ss";
				const date = (isCancel) ? data.orderCancelledDatetime :
					data.paymentDatetime || data.paymentCompletedDatetime;
				return (date) ? new Date(date).format(dateFormat) : null;
			};
			const getPaymentDateLabel = () => {
				return (isCancel) ? "취소일시" : "결제일시";
			};
			const getPaymentKind = () => {
				switch(data.paymentClassification) {
					case "ORDER" : return <Badge color="green">판매 결제</Badge>;
					case "RECEIVABLES" : return <Badge color="red">미수금 결제</Badge>;
					case "REPAYMENT" : return <Badge color="yellow">환불 시 재결제</Badge>;
					case "TRANSFER" : return <Badge color="purple">양도 수수료</Badge>;
					case "CANCELLED" : return <Badge color="red">판매 취소</Badge>;
				}
				return "-";
			};
			const getPaymentType = () => {
				switch(data.paymentType) {
					case "CARD" : return `카드 ${data.installmentPeriod || 0}개월`;
					case "TRANSFER" : return "이체";
					case "CASH" : return "현금";
				}
			};

			// 같은 주문의 결제가 2건 이상이면 결제 내역을 한 줄로 붙인다(목록 카드 금액은 묶인 결제의 합이다)
			const getOrderPayments = () => {
				const orderPayments = (data.orderPayments || []).filter(p => p);
				if(orderPayments.length < 2) {
					return null;
				}
				const paymentsText = orderPayments.map(p => {
					const status = p.displayStatus || ((p.paymentStatus == "PAYMENT_WAIT") ? "WAIT" : (p.paymentStatus == "PAYMENT_CANCELLED") ? "CANCELLED" : "");
					const statusText = (status == "WAIT") ? "결제대기 " : (status == "CANCELLED") ? "취소 " : "";
					return getPaymentTypeName(p.paymentType) + " " + statusText + getCommaNumber(p.paymentAmount);
				}).join(" · ");
				// 합계는 낸 금액(paidAmount)의 합이다
				const totalAmount = orderPayments.reduce((sum, p) => sum + (Number(p.paidAmount) || 0), 0);
				return (
					<li><div>결제 내역</div><div>{paymentsText} (합계 {getCommaNumber(totalAmount)}원)</div></li>
				);
			};

			const paymentDateTime = getPaymentDateTime();

			return (
				<div className="paymentInfo">
					<h4>결제 정보</h4>
					<ul>
						{getOrderPayments()}
						{(paymentDateTime) && <li><div>{getPaymentDateLabel()}</div><div>{paymentDateTime}</div></li>}
						<li><div>결제분류</div><div>{getPaymentKind()}</div></li>
						<li><div>결제금액</div><div className={paymentColor}><b>{paymentAmount}</b></div></li>
						<li><div>결제정보</div><div>{getPaymentType()}</div></li>
						<li><div>회원명</div><div>{data.name}</div></li>
						<li><div>결제 담당자</div><div>{data.coachName}</div></li>
					</ul>
				</div>
			);
		};

		return (
			<PullToRefresh
				onRefresh={eventHandler.pullToRefresh}
				resistance={2}
				pullingContent={<></>}
				refreshingContent={<PullToRefreshing />}
			>
				<MainLayout
					withHeader={true}
					headerProps={{
						withBackButton : true,
						centerTitle : "상세 내역"
					}}
					backgroundColor="white"
					withBottomTab={false}
					isLoading={isLoading}
					isError={isError}
				>
					<PaymentForm>
						<div className="color">
							{getTitle()}
							{getPaymentInfo()}
							<PassInfo data={data} />
						</div>
						<Button color="blue" onClick={() => {navigate(-1)}}>확인</Button>
					</PaymentForm>
				</MainLayout>
			</PullToRefresh>
		);
	};
	return render();
};

export default PaymentInfo;